/**
 * WeekPreview Component
 * 
 * A week view calendar component for displaying court events.
 * 
 * Features:
 * - Displays court events from the CourtEvent table in a week view
 * - Uses react-big-calendar with luxon for date handling
 * - Fixed to week view for focused display
 * - Click events to show detailed modals (read-only)
 * - Event modal displays CourtEvent details
 * - Suitable for both managers and users (no editing capabilities)
 * 
 * @param props.courtEvents - Array of CourtEventDto objects to display on the calendar
 * @param props.currentDate - Optional date to center the week view on (defaults to today)
 * @param props.minShownTime - Optional minimum time to show on the calendar (format: "HH:mm", e.g., "07:00")
 * @param props.maxShownTime - Optional maximum time to show on the calendar (format: "HH:mm", e.g., "19:00")
 * 
 * Usage Example:
 * ```tsx
 * const [events, setEvents] = useState<CourtEventDto[]>([]);
 * 
 * <WeekPreview 
 *   courtEvents={events}
 *   currentDate={new Date()}
 *   minShownTime="07:00"
 *   maxShownTime="19:00"
 * />
 * ```
 */

import React, { useState, useMemo } from "react";
import { Calendar, luxonLocalizer, Views, Event } from 'react-big-calendar';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { CourtEventDto, FullCourtEventDto } from "../../api/CourtEventApi";
import CourtEventApi from "../../api/CourtEventApi";
import './WeekPreview.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Setup the localizer for react-big-calendar using luxon
const localizer = luxonLocalizer(DateTime);

// Extended event interface for the calendar
interface WeekEvent extends Event {
  courtEvent: CourtEventDto;
  resource?: any;
}

const WeekPreview = (props: {
  courtEvents: CourtEventDto[];
  currentDate?: Date;
  minShownTime?: string; // Format: "HH:mm" (e.g., "07:00")
  maxShownTime?: string; // Format: "HH:mm" (e.g., "19:00")
}) => {
  const [selectedEvent, setSelectedEvent] = useState<CourtEventDto | null>(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullEventData, setFullEventData] = useState<FullCourtEventDto | null>(null);

  // Parse time strings to create min/max time bounds for the calendar
  const getTimeFromString = (timeString: string): Date => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const minTime = props.minShownTime ? getTimeFromString(props.minShownTime) : undefined;
  const maxTime = props.maxShownTime ? getTimeFromString(props.maxShownTime) : undefined;

  // No time prefix: react-big-calendar already renders its own "5:00 AM – 5:30 AM" label above the
  // title in week/day view, so prefixing the time printed it twice and pushed the case name out of
  // the visible box (same fix as CalendarEditable.tsx).
  const formatEventTitle = (courtEvent: CourtEventDto) => {
    return courtEvent.CourtEvent_Title || 'Court Event';
  };

  /** Native tooltip so a clipped or narrow title is still readable on hover. */
  const eventTooltip = (event: WeekEvent) => {
    const start = DateTime.fromMillis(parseInt('' + event.courtEvent.CourtEvent_Start));
    return `${start.toFormat('ccc d LLL, h:mm a')} — ${event.courtEvent.CourtEvent_Title || 'Court Event'}`;
  };

  // Transform court events into calendar events
  const weekEvents: WeekEvent[] = useMemo(() => {
    return props.courtEvents.map(courtEvent => {
      const startDate = DateTime.fromMillis(parseInt('' + courtEvent.CourtEvent_Start));
      const endDate = DateTime.fromMillis(parseInt('' + courtEvent.CourtEvent_End));

      return {
        id: courtEvent.CourtEvent_guid,
        title: formatEventTitle(courtEvent),
        start: startDate.toJSDate(),
        end: endDate.toJSDate(),
        courtEvent: courtEvent,
        resource: courtEvent
      };
    });
  }, [props.courtEvents]);

  const handleSelectEvent = async (event: WeekEvent) => {
    setSelectedEvent(event.courtEvent);
    setEventModalOpen(true);
    setLoading(true);
    setError(null);
    setFullEventData(null);

    try {
      // Fetch the full event data from the API
      const fullEventData = await CourtEventApi.getCourtEvent(event.courtEvent.CourtEvent_guid);
      setFullEventData(fullEventData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setEventModalOpen(false);
    setSelectedEvent(null);
    setFullEventData(null);
    setError(null);
  };

  const formatDateTime = (timestamp: number) => {
    return DateTime.fromMillis(parseInt('' + timestamp)).toLocaleString(DateTime.DATETIME_MED);
  };

  const formatDate = (timestamp: number) => {
    return DateTime.fromMillis(parseInt('' + timestamp)).toLocaleString(DateTime.DATE_FULL);
  };

  const formatTime = (timestamp: number) => {
    return DateTime.fromMillis(parseInt('' + timestamp)).toFormat('HH:mm');
  };

  const eventStyleGetter = (event: WeekEvent) => {
    // Color code events based on their properties or status
    let backgroundColor = '#1976d2'; // Default blue
    
    // You can customize colors based on event properties
    if (event.courtEvent.CourtEvent_Title?.toLowerCase().includes('hearing')) {
      backgroundColor = '#f57c00'; // Orange for hearings
    } else if (event.courtEvent.CourtEvent_Title?.toLowerCase().includes('trial')) {
      backgroundColor = '#d32f2f'; // Red for trials
    } else if (event.courtEvent.CourtEvent_Title?.toLowerCase().includes('meeting')) {
      backgroundColor = '#388e3c'; // Green for meetings
    }
    
    const style = {
      backgroundColor,
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block',
      fontSize: '12px',
      padding: '2px 4px'
    };
    return { style };
  };

  return (
    <Box className="week-preview">
      <Box className="week-preview-container" sx={{ height: 500 }}>
        <Calendar
          localizer={localizer}
          events={weekEvents}
          startAccessor="start"
          endAccessor="end"
          views={[Views.WEEK]}
          defaultView={Views.WEEK}
          view={Views.WEEK}
          onView={() => {}} // Disable view changes
          date={props.currentDate || new Date()}
          onNavigate={() => {}} // Disable navigation for this preview
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          tooltipAccessor={eventTooltip}
          popup={false}
          showMultiDayTimes
          step={30}
          timeslots={1}
          // Concurrent hearings sit side by side instead of shrinking toward overlap, so a court
          // date behind another one is still legible rather than a sliver of unreadable text
          // (same fix as CalendarEditable.tsx).
          dayLayoutAlgorithm="no-overlap"
          className="custom-week-preview"
          toolbar={false} // Hide toolbar for cleaner preview
          min={minTime}
          max={maxTime}
          formats={{
            dayHeaderFormat: (date, culture, localizer) => 
              localizer?.format(date, 'dddd M/D', culture) || '',
            timeGutterFormat: (date, culture, localizer) =>
              localizer?.format(date, 'HH:mm', culture) || '',
          }}
        />
      </Box>

      {/* Event Details Modal */}
      <Dialog 
        open={eventModalOpen} 
        onClose={handleCloseModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '300px' }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              Court Event Details
            </Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {selectedEvent && !loading && (
            <Box>
              <Stack spacing={3}>
                {/* Court Event Information */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Event Information
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Title
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedEvent.CourtEvent_Title || 'No title'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={4}>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Date
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(selectedEvent.CourtEvent_Start)}
                          </Typography>
                        </Box>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Start Time
                          </Typography>
                          <Typography variant="body1">
                            {formatTime(selectedEvent.CourtEvent_Start)}
                          </Typography>
                        </Box>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            End Time
                          </Typography>
                          <Typography variant="body1">
                            {formatTime(selectedEvent.CourtEvent_End)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Description
                        </Typography>
                        <Typography variant="body1">
                          {selectedEvent.CourtEvent_Desc || 'No description'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Event ID
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', backgroundColor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                          {selectedEvent.CourtEvent_guid}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Ticket Information */}
                {fullEventData?.ticket && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        Associated Ticket
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Ticket Number
                          </Typography>
                          <Typography variant="body1">
                            {fullEventData.ticket.Ticket_Number}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Ticket ID
                          </Typography>
                          <Typography variant="body1">
                            {fullEventData.ticket.Ticket_guid}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Citation
                          </Typography>
                          <Typography variant="body1">
                            {fullEventData.ticket.Ticket_Citation}
                          </Typography>
                        </Box>
                        {fullEventData?.user && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              Client Name
                            </Typography>
                            <Typography variant="body1">
                              {fullEventData.user.User_FirstName} {fullEventData.user.User_LastName}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                      {fullEventData?.ticket?.Ticket_guid && (
                        <Button
                          component={Link}
                          to={`/ticket?Ticket_guid=${fullEventData.ticket.Ticket_guid}`}
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 2, mr: 'auto' }} // Push to the left side
                        >
                          View Ticket
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeekPreview;